import { skipToken, useQuery } from "@tanstack/vue-query";
import { paramsToKey } from "@verbleif/lib/src/composables/Query/hydraInfinite";
import { createGlobalState } from "@vueuse/core";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "./AuthStore/useAuthStore";
import { useServerErrorHandler } from "./Error/serverErrorHandler";
import { useLocationStore } from "./LocationStore/useLocationStore";
import { useNotificationEndpoints } from "./Notifications/useNotificationEndpoints";
import {
  scopedUserNotificationUnreadCount,
  unreadNotificationsCountQueryOptions,
  userNotificationUnreadCountQueryOptions,
} from "./Notifications/v2/notifications.api";
import { useUserNotificationsLive } from "./Notifications/v2/useUserNotificationsLive";
import { useResourceScope } from "./Query/resourceKeys";

export const useNavbarUnreadCount = createGlobalState(() => {
  const { collectionEndpoint, isV2Enabled } = useNotificationEndpoints();
  const authStore = useAuthStore();
  const locationStore = useLocationStore();
  const { onError } = useServerErrorHandler();
  const { t } = useI18n();
  const scope = useResourceScope();
  // Keeps the v2 count (and the open inbox lists) current over Mercure for as long as the navbar count exists.
  useUserNotificationsLive();

  const legacyParams = computed(() => {
    const next = new URLSearchParams();
    next.set("perPage", "1");
    next.set("page", "1");
    next.set("exists[readAt]", "false");
    next.set("active", "true");
    if (collectionEndpoint.value) {
      next.set("endpoint", collectionEndpoint.value);
    }
    return next;
  });
  const legacyQuery = useQuery(computed(() => {
    const endpoint = isV2Enabled.value ? "" : collectionEndpoint.value;
    const options = unreadNotificationsCountQueryOptions(scope.value, endpoint, paramsToKey(legacyParams.value));
    return {
      queryKey: options.queryKey,
      queryFn: endpoint ? options.queryFn : skipToken,
      staleTime: 30_000,
    };
  }));

  // The v2 inbox has one count per user, split per location; Mercure keeps it current.
  const inboxQuery = useQuery(computed(() => {
    const userId = isV2Enabled.value ? authStore.user.value?.id ?? null : null;
    const options = userNotificationUnreadCountQueryOptions(userId ?? 0);
    return {
      queryKey: options.queryKey,
      queryFn: userId ? options.queryFn : skipToken,
      staleTime: 30_000,
    };
  }));

  for (const query of [legacyQuery, inboxQuery]) {
    watch(() => query.isError.value, (isError) => {
      if (isError && query.error.value) {
        onError({ error: query.error.value, toastTitle: t("navbar.dropdown.pull_notification_count_title") });
      }
    });
  }

  // The v2 count follows the selected location or organisation, matching its list and "mark all read".
  const unreadCount = computed(() => {
    if (!isV2Enabled.value) {
      return legacyQuery.data.value ?? 0;
    }
    return scopedUserNotificationUnreadCount(
      inboxQuery.data.value,
      scope.value,
      locationIri => locationStore.locations.value[locationIri]?.client ?? null,
    );
  });

  async function checkUnreadCount(): Promise<void> {
    if (isV2Enabled.value) {
      await inboxQuery.refetch();
      return;
    }
    await legacyQuery.refetch();
  }

  return {
    unreadCount,
    checkUnreadCount,
  };
});
